import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { BonusStat, EquipmentSlot, EquipmentStats, SkillId } from '@/domain/types';

export type LeaderboardCategory = 'item_power' | 'level' | 'vault_tier' | 'credits' | 'endless_wave';

export interface LeaderboardEntry {
  id: string;
  runnerName: string;
  level: number;
  xp: number;
  credits: number;
  highestVaultTier: number;
  itemPower: number;
  /** Best wave reached in Endless Fight (0 if never played). Sourced from
   *  profile meta JSONB when the endless_wave category is active. */
  bestEndlessWave: number;
  rank: number;
  avatarColor: string;
}

export interface PlayerEquipmentSlot {
  id: string;
  slot: EquipmentSlot;
  itemId: string;
  upgradeLevel: number;
  rolledStats?: EquipmentStats;
  bonusStats?: BonusStat[];
}

export interface PlayerProfileData {
  id: string;
  name: string;
  level: number;
  credits: number;
  highestVaultTier: number;
  itemPower: number;
  skillSlots: [SkillId | null, SkillId | null];
  equipment: PlayerEquipmentSlot[];
  avatarColor: string;
}

interface LeaderboardState {
  entries: LeaderboardEntry[];
  category: LeaderboardCategory;
  loading: boolean;
  error: string | null;
  lastFetchAt: number;
  myRank: number | null;

  selectedProfile: PlayerProfileData | null;
  profileLoading: boolean;

  setCategory: (cat: LeaderboardCategory) => void;
  fetch: (myId?: string) => Promise<void>;
  fetchPlayerProfile: (playerId: string) => Promise<void>;
  clearSelectedProfile: () => void;
}

const ORDER_MAP: Record<LeaderboardCategory, string[]> = {
  item_power: ['item_power', 'level'],
  level:      ['level', 'xp'],
  vault_tier: ['highest_vault_tier', 'level'],
  credits:    ['credits'],
  // Endless uses a different (profiles-table) query path entirely — see
  // fetchEndless() below. This entry exists only to satisfy the typing.
  endless_wave: [],
};

export const useLeaderboardStore = create<LeaderboardState>()((set, get) => ({
  entries: [],
  category: 'item_power',
  loading: false,
  error: null,
  lastFetchAt: 0,
  myRank: null,
  selectedProfile: null,
  profileLoading: false,

  setCategory: (cat) => set({ category: cat }),

  clearSelectedProfile: () => set({ selectedProfile: null }),

  fetch: async (myId) => {
    set({ loading: true, error: null });
    try {
      const { category } = get();

      // ── Endless leaderboard is a SEPARATE query path ──────────
      // The leaderboard_view doesn't expose bestEndlessWave (lives in
      // profiles.meta JSONB). We query profiles directly + sort on the
      // JSONB key. Keeps the existing 4 categories on the optimized view.
      if (category === 'endless_wave') {
        // Multi-char (migration 009): runner_name / level / xp / credits /
        // meta all live on `characters` now. Each character row is its own
        // leaderboard entry; the user behind it is `characters.user_id`.
        const { data, error } = await supabase
          .from('characters')
          .select('id, name, level, xp, credits, meta')
          .order('meta->bestEndlessWave', { ascending: false })
          .limit(100);
        if (error) throw new Error(`leaderboard fetch (endless_wave): ${error.message}`);
        type Row = { id: string; name: string; level: number; xp: number; credits: number; meta: Record<string, unknown> | null };
        const rows = (data ?? []) as Row[];
        const entries: LeaderboardEntry[] = rows
          .map((r, i) => {
            const meta = r.meta ?? {};
            const wave = Number(meta.bestEndlessWave ?? 0);
            return {
              id: r.id,
              runnerName: r.name,
              level: r.level,
              xp: r.xp,
              credits: r.credits,
              highestVaultTier: Number(meta.highestVaultTier ?? 0),
              itemPower: Number(meta.itemPower ?? 0),
              bestEndlessWave: wave,
              rank: i + 1,
              avatarColor: String(meta.avatarColor ?? 'blue'),
            };
          })
          .filter((e) => e.bestEndlessWave > 0);
        // Re-rank after filter so positions 1..N stay dense
        entries.forEach((e, i) => { e.rank = i + 1; });
        const myRank = myId ? (entries.find((e) => e.id === myId)?.rank ?? null) : null;
        set({ entries, myRank, lastFetchAt: Date.now(), loading: false });
        return;
      }

      const [primaryCol, secondaryCol] = ORDER_MAP[category];

      let query = supabase
        .from('leaderboard_view')
        .select('id, runner_name, level, xp, credits, highest_vault_tier, item_power')
        .order(primaryCol, { ascending: false })
        .limit(100);

      if (secondaryCol) {
        query = query.order(secondaryCol, { ascending: false });
      }

      const { data, error } = await query;
      // S-8: label errors with the context — bare Supabase errors lack info
      // about which query failed when stack-traced.
      if (error) throw new Error(`leaderboard fetch (${primaryCol}): ${error.message}`);

      const rows = (data ?? []) as Array<{
        id: string; runner_name: string; level: number; xp: number;
        credits: number; highest_vault_tier: number; item_power: number;
      }>;

      const entries: LeaderboardEntry[] = rows.map((r, i) => ({
        id: r.id,
        runnerName: r.runner_name,
        level: r.level,
        xp: r.xp,
        credits: r.credits,
        highestVaultTier: r.highest_vault_tier ?? 0,
        itemPower: r.item_power ?? 0,
        bestEndlessWave: 0,  // not in the optimized view; populated only on endless_wave category
        rank: i + 1,
        avatarColor: 'blue',
      }));

      let myRank: number | null = null;
      if (myId) {
        const found = entries.find((e) => e.id === myId);
        if (found) {
          myRank = found.rank;
        } else {
          const lastVal = rows[rows.length - 1]?.[primaryCol as keyof typeof rows[0]] ?? 0;
          const { count } = await supabase
            .from('leaderboard_view')
            .select('id', { count: 'exact', head: true })
            .gt(primaryCol, lastVal);
          myRank = (count ?? 0) + 1;
        }
      }

      set({ entries, myRank, lastFetchAt: Date.now(), loading: false });
    } catch (err) {
      set({ error: (err as Error).message ?? 'Fehler', loading: false });
    }
  },

  fetchPlayerProfile: async (playerId) => {
    set({ profileLoading: true, selectedProfile: null });
    try {
      // playerId from a leaderboard click is the CHARACTER id (the view's
      // `id` column is `characters.id`). Query characters + equipment by
      // character_id (renamed from player_id in migration 009).
      const [profileRes, eqRes] = await Promise.all([
        supabase
          .from('characters')
          .select('id, name, level, credits, meta')
          .eq('id', playerId)
          .single(),
        supabase
          .from('equipment_instances')
          .select('id, item_id, upgrade_level, equipped_slot, meta')
          .eq('character_id', playerId)
          .not('equipped_slot', 'is', null),
      ]);

      if (profileRes.error) throw profileRes.error;
      const profile = profileRes.data;
      const meta = (profile.meta ?? {}) as Record<string, unknown>;

      const eqRows = (eqRes.data ?? []) as Array<{
        id: string; item_id: string; upgrade_level: number;
        equipped_slot: string; meta: Record<string, unknown> | null;
      }>;

      const equipment: PlayerEquipmentSlot[] = eqRows.map((row) => {
        const rowMeta = row.meta as Record<string, unknown> | null;
        return {
          id: row.id,
          slot: row.equipped_slot as EquipmentSlot,
          itemId: row.item_id,
          upgradeLevel: row.upgrade_level,
          rolledStats: rowMeta?.rolledStats as EquipmentStats | undefined,
          bonusStats: rowMeta?.bonusStats as BonusStat[] | undefined,
        };
      });

      set({
        selectedProfile: {
          id: profile.id,
          name: profile.name,
          level: profile.level,
          credits: profile.credits,
          highestVaultTier: (meta.highestVaultTier as number) ?? 0,
          itemPower: (meta.itemPower as number) ?? 0,
          skillSlots: (meta.skillSlots as [SkillId | null, SkillId | null]) ?? [null, null],
          equipment,
          avatarColor: (meta.avatarColor as string) ?? 'blue',
        },
        profileLoading: false,
      });
    } catch {
      set({ profileLoading: false });
    }
  },
}));
